import { useCallback } from "react"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/material"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { OrderType } from "../../../../../modules/payments/orderTypes"
import { deleteProductFavorite, saveProductFavorite } from "../../createOrderApi"

import type { TakeawayProduct } from "./takeawayTypes"
import { TakeawayQuery } from "./takeawayApi"
import { useTakeawayCart } from "./TakeawayCartContext"
import { TakeawayPage } from "./TakeawayPage"
import { TakeawayBasketDrawer } from "./components/TakeawayBasketDrawer"

const useStyles = makeStyles()(() => ({
    root: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        width: "100%",
        minHeight: 0,
    },
}))

type Props = Readonly<{
    onClose: () => void
}>

export function TakeawayFormContainer({ onClose }: Props) {
    const { classes } = useStyles()
    const cart = useTakeawayCart()

    const { mutateAsync: callSaveFavorite } = useApiMutation(saveProductFavorite, {
        async onSuccess(_, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TakeawayQuery.TAKEAWAY_MENU] })
        },
        async onError() {},
    })
    const { mutateAsync: callDeleteFavorite } = useApiMutation(deleteProductFavorite, {
        async onSuccess(_, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TakeawayQuery.TAKEAWAY_MENU] })
        },
        async onError() {},
    })

    const handleFavoriteToggle = useCallback(
        async (product: TakeawayProduct, makeFavorite: boolean): Promise<boolean> => {
            try {
                if (makeFavorite) {
                    await callSaveFavorite({ id: product.id, productType: product.productType ?? OrderType.TAKE_AWAY })
                } else {
                    // Use userDetails.favoriteId (the favorites record ID) as mobile does
                    const favoriteId = product.userDetails?.favoriteId ?? product.id
                    await callDeleteFavorite({ productId: favoriteId, body: { id: favoriteId, productType: product.productType ?? OrderType.TAKE_AWAY } })
                }
                return true
            } catch {
                return false
            }
        },
        [callSaveFavorite, callDeleteFavorite],
    )

    if (!cart?.selectedKitchen) return null

    const {
        selectedKitchen,
        selectedDate,
        cartByDate,
        cartForSelectedDate,
        showCheckout,
        onQuantityChange,
        onCloseCheckout,
        onClearCart,
    } = cart

    return (
        <Box className={classes.root}>
            <TakeawayPage
                kitchen={selectedKitchen}
                selectedDate={selectedDate}
                cart={cartForSelectedDate}
                onDateChange={cart.onDateChange}
                onQuantityChange={onQuantityChange}
                onFavoriteToggle={handleFavoriteToggle}
            />

            <TakeawayBasketDrawer
                open={showCheckout}
                cartByDate={cartByDate}
                kitchen={selectedKitchen}
                onQuantityChange={onQuantityChange}
                onClose={onCloseCheckout}
                onSuccess={() => {
                    onClearCart()
                    onCloseCheckout()
                    onClose()
                }}
            />
        </Box>
    )
}
